/**
 * Supplier names (ADR-0026 §5). Purchase history names suppliers in free text with no id or VAT
 * number: `ACME S.R.L.`, `ACME SRL`, `Acme Srl **` are one company; `ACME S.A.S.` may not be.
 *
 * The **match key** folds what never distinguishes two companies (case, accents, punctuation,
 * spacing, trailing `*` markers, the spelling of the legal form) and keeps the legal form itself.
 * Same key ⇒ same supplier, merged automatically. Anything looser is only a *look-alike* that a
 * person confirms (`lookAlikeReason`).
 */

/** Spellings of a legal form (dots and spaces already removed) → canonical token. */
const LEGAL_FORMS: Record<string, string> = {
  SRL: "SRL",
  SRLS: "SRLS",
  SPA: "SPA",
  SAPA: "SAPA",
  SAS: "SAS",
  SNC: "SNC",
  SS: "SS",
  SCARL: "SCARL",
  SCRL: "SCARL",
  SCPA: "SCPA",
  SC: "SC",
  COOP: "COOP",
  LTD: "LTD",
  LIMITED: "LTD",
  PLC: "PLC",
  LLP: "LLP",
  GMBH: "GMBH",
  AG: "AG",
  KG: "KG",
  SA: "SA",
  SARL: "SARL",
  SL: "SL",
  SLU: "SLU",
  BV: "BV",
  NV: "NV",
  INC: "INC",
  LLC: "LLC",
  CORP: "CORP",
  SE: "SE",
  SPRL: "SPRL",
  SRO: "SRO",
  AB: "AB",
  OY: "OY",
};

/** Legal forms written out in words (Italian registry style). */
const LEGAL_FORM_PHRASES: [string[], string][] = [
  [["SOCIETA", "A", "RESPONSABILITA", "LIMITATA", "SEMPLIFICATA"], "SRLS"],
  [["SOCIETA", "A", "RESPONSABILITA", "LIMITATA"], "SRL"],
  [["SOCIETA", "PER", "AZIONI"], "SPA"],
  [["SOCIETA", "IN", "ACCOMANDITA", "SEMPLICE"], "SAS"],
  [["SOCIETA", "IN", "NOME", "COLLETTIVO"], "SNC"],
];

/** Qualifiers that may follow the legal form and never distinguish companies. */
const TRAILING_QUALIFIERS = [["UNIPERSONALE"], ["A", "SOCIO", "UNICO"], ["SOCIO", "UNICO"], ["IN", "LIQUIDAZIONE"]];

export interface SupplierName {
  /** The words of the name without the legal form (upper case, letters and digits). */
  words: string[];
  /** The words joined: what two spellings of one company have in common. */
  base: string;
  /** Canonical legal form (`SRL`, `SPA`, …) or null when the name has none. */
  legalForm: string | null;
  /** `base|legalForm` — equal keys are the same supplier. */
  matchKey: string;
}

/** The name as shown: trimmed, inner spaces collapsed, trailing `*` markers removed. */
export function supplierDisplayName(raw: string): string {
  return raw
    .replace(/\s+/g, " ")
    .replace(/[\s*]+$/, "")
    .trim();
}

function tokens(raw: string): string[] {
  const upper = raw
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .toUpperCase()
    .replace(/\*/g, " ");
  const list = upper
    .split(/[^A-Z0-9.']+/)
    .map((t) => t.replace(/[.']/g, ""))
    .filter(Boolean);
  // "S R L" (spaced initials) → "SRL".
  let initials = 0;
  while (initials < list.length && list[list.length - 1 - initials]?.length === 1) initials++;
  if (initials >= 2) list.splice(list.length - initials, initials, list.slice(list.length - initials).join(""));
  return list;
}

function endsWith(list: string[], tail: string[]): boolean {
  return tail.length < list.length && tail.every((t, i) => list[list.length - tail.length + i] === t);
}

/**
 * Removes the legal form from the end of `list` and returns it (canonical), or null. Short tokens
 * may spell one form between them (`S.A R.L.` → `SARL`); a name is never reduced to nothing.
 */
function takeLegalForm(list: string[]): string | null {
  for (const [phrase, form] of LEGAL_FORM_PHRASES) {
    if (endsWith(list, phrase)) {
      list.splice(list.length - phrase.length, phrase.length);
      return form;
    }
  }
  for (let n = Math.min(3, list.length - 1); n >= 1; n--) {
    const tail = list.slice(list.length - n);
    if (n > 1 && tail.some((t) => t.length > 3)) continue;
    const form = LEGAL_FORMS[tail.join("")];
    if (form) {
      list.splice(list.length - n, n);
      return form;
    }
  }
  return null;
}

export function parseSupplierName(raw: string): SupplierName {
  const list = tokens(raw);
  for (const qualifier of TRAILING_QUALIFIERS) {
    if (endsWith(list, qualifier)) list.splice(list.length - qualifier.length, qualifier.length);
  }
  const legalForm = takeLegalForm(list);
  const base = list.join("");
  return { words: list, base, legalForm, matchKey: `${base}|${legalForm ?? ""}` };
}

export const LOOK_ALIKE_REASONS = ["legal_form", "prefix", "spelling"] as const;
export type LookAlikeReason = (typeof LOOK_ALIKE_REASONS)[number];

/** Shortest name for the prefix rule (shorter prefixes are too common to mean anything). */
export const LOOK_ALIKE_MIN_PREFIX = 5;
/** Shortest name for the spelling rule; one edit allowed, two from LOOK_ALIKE_LONG_NAME characters. */
export const LOOK_ALIKE_MIN_SPELLING = 8;
export const LOOK_ALIKE_LONG_NAME = 12;

/** Levenshtein distance, giving up (returning max + 1) once it exceeds `max`. */
export function boundedEditDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const value = Math.min((previous[j] ?? 0) + 1, (current[j - 1] ?? 0) + 1, (previous[j - 1] ?? 0) + cost);
      current.push(value);
      if (value < rowMin) rowMin = value;
    }
    if (rowMin > max) return max + 1;
    previous = current;
  }
  return previous[b.length] ?? max + 1;
}

/**
 * Why two suppliers with different match keys might be one company, or null. The rules
 * (ADR-0026 §5): the same name with another legal form; the words of one name starting the
 * other (≥ 5 characters: `PELLIZZARI` / `PELLIZZARI SICUREZZA`, never `INFORMA` / `INFORMATICA`);
 * or a spelling one edit away (names of ≥ 8 characters; two edits from 12).
 */
export function lookAlikeReason(a: SupplierName, b: SupplierName): LookAlikeReason | null {
  if (a.matchKey === b.matchKey || !a.base || !b.base) return null;
  if (a.base === b.base) return "legal_form";
  const [short, long] = a.words.length <= b.words.length ? [a, b] : [b, a];
  if (
    short.base.length >= LOOK_ALIKE_MIN_PREFIX &&
    short.words.length < long.words.length &&
    short.words.every((w, i) => long.words[i] === w)
  ) {
    return "prefix";
  }
  const shortest = Math.min(a.base.length, b.base.length);
  if (shortest >= LOOK_ALIKE_MIN_SPELLING) {
    const max = shortest >= LOOK_ALIKE_LONG_NAME ? 2 : 1;
    if (boundedEditDistance(a.base, b.base, max) <= max) return "spelling";
  }
  return null;
}
